/**
 * Tax Rebate Calculation and Optimization Engine (Svelte Client)
 * Sub-millisecond client-side greedy piecewise marginal yield allocator.
 */

/**
 * Parses condition range from localized strings e.g. "HK$10,000 - HK$29,999", "$200,000+"
 * @param {string} condition
 * @returns {{min: number, max: number}}
 */
export function parseConditionRange(condition) {
  if (!condition) return { min: 0, max: 9999999 };
  const str = String(condition).trim();
  const cleaned = str.replace(/,/g, '');
  const matches = cleaned.match(/\d+(?:\.\d+)?/g);
  if (!matches || matches.length === 0) return { min: 0, max: 9999999 };

  const nums = matches.map(Number);
  if (nums.length >= 2) {
    return { min: Math.min(nums[0], nums[1]), max: Math.max(nums[0], nums[1]) };
  }

  const lower = cleaned.toLowerCase();
  const isBelow = lower.includes('under') ||
    lower.includes('below') ||
    lower.includes('<') ||
    lower.includes('以下') ||
    lower.includes('up to');

  if (isBelow) {
    return { min: 0, max: nums[0] };
  }
  return { min: nums[0], max: 9999999 };
}

/**
 * Parses raw reward tier into structured tier
 * @param {Object} tier
 * @returns {Object}
 */
export function parseRewardTier(tier) {
  const condition = String(tier.condition || '');
  const range = parseConditionRange(condition);
  const rType = String(tier.rewardType || '').toLowerCase() === 'percentage' ? 'Percentage' : 'Fixed';
  const fixed = Number(tier.fixedAmount) || Number(tier.topupAmount) || 0;
  const topup = Number(tier.topupAmount) || 0;
  const pct = Number(tier.percentage) || 0;
  const maxR = Number(tier.maxRewardAmount) || 0;
  const uom = String(tier.unitOfMeasure || '$');

  return {
    minSpending: range.min,
    maxSpending: range.max,
    rewardType: rType,
    fixedAmount: fixed,
    percentage: pct,
    topupAmount: topup,
    maxRewardAmount: maxR,
    unitOfMeasure: uom,
    conditionText: condition
  };
}

/**
 * Identifies the bank or platform group for a promotion
 * @param {Object} promo
 * @returns {string}
 */
export function getBankOrPlatformGroup(promo) {
  if (!promo) return 'Other';
  if (Array.isArray(promo.banks) && promo.banks.length > 0) {
    const b = String(promo.banks[0]).toUpperCase();
    if (b.includes('CCBA') || b.includes('建行') || b.includes('CHINA CONSTRUCTION')) return 'CCB Asia (建行亞洲)';
    if (b.includes('DBS') || b.includes('星展')) return 'DBS (星展)';
    if (b.includes('BEA') || b.includes('東亞')) return 'Bank of East Asia (東亞銀行)';
    if (b.includes('HSBC') || b.includes('滙豐') || b.includes('汇丰')) return 'HSBC (滙豐)';
    if (b.includes('BOC') || b.includes('中銀') || b.includes('中银')) return 'BOC (中銀香港)';
    if (b.includes('SCB') || b.includes('渣打') || b.includes('STANDARD CHARTERED')) return 'Standard Chartered (渣打)';
    if (b.includes('HANG SENG') || b.includes('恒生')) return 'Hang Seng (恒生)';
    if (b.includes('CITI') || b.includes('花旗')) return 'Citibank (花旗)';
    return promo.banks[0];
  }
  const text = `${promo.brand || ''} ${promo.name || ''} ${promo.description || ''}`.toUpperCase();
  if (text.includes('CCBA') || text.includes('建行') || text.includes('CHINA CONSTRUCTION')) {
    return 'CCB Asia (建行亞洲)';
  }
  if (text.includes('ALIPAY') || text.includes('支付寶') || text.includes('支付宝')) {
    return 'AlipayHK (支付寶)';
  }
  if (text.includes('WECHAT') || text.includes('微信')) {
    return 'WeChat Pay (微信支付)';
  }
  if (text.includes('DBS') || text.includes('星展')) {
    return 'DBS (星展)';
  }
  if (text.includes('EAST ASIA') || text.includes('BEA') || text.includes('東亞') || text.includes('东亚')) {
    return 'Bank of East Asia (東亞銀行)';
  }
  if (text.includes('HSBC') || text.includes('滙豐') || text.includes('汇丰')) {
    return 'HSBC (滙豐)';
  }
  if (text.includes('BOC') || text.includes('中銀') || text.includes('中银')) {
    return 'BOC (中銀香港)';
  }
  if (text.includes('STANDARD CHARTERED') || text.includes('渣打')) {
    return 'Standard Chartered (渣打)';
  }
  if (text.includes('HANG SENG') || text.includes('恒生')) {
    return 'Hang Seng (恒生)';
  }
  if (text.includes('CITI') || text.includes('花旗')) {
    return 'Citibank (花旗)';
  }
  if (promo.brand && promo.brand !== 'Promotion') {
    return promo.brand;
  }
  return promo.id || 'Other';
}

/**
 * Normalize raw promotion & formula objects into standard TaxPromotion
 * @param {Object} promo 
 * @param {Object} [formula] 
 * @returns {Object}
 */
export function normalizeTaxPromotion(promo, formula) {
  const id = promo._id || promo.id || '';
  const name = promo.name || '';
  const desc = promo.description || '';
  const banks = Array.isArray(promo.banks) ? promo.banks : [];
  const parentId = promo.parentId || '';

  // Derive brand if missing or empty in MongoDB
  let brand = promo.brand || '';
  if (!brand || brand.trim() === '') {
    if (Array.isArray(promo.banks) && promo.banks.length > 0) {
      const bank = String(promo.banks[0]);
      if (bank.includes('DBS')) brand = 'DBS';
      else if (bank.includes('BEA') || bank.includes('東亞')) brand = 'Bank of East Asia';
      else brand = bank;
    } else if (name.toLowerCase().includes('alipay')) {
      brand = 'AlipayHK';
    } else if (name.toLowerCase().includes('wechat')) {
      brand = 'WeChat Pay';
    } else if (name.toLowerCase().includes('dbs')) {
      brand = 'DBS';
    } else if (name.toLowerCase().includes('east asia') || name.toLowerCase().includes('bea') || name.includes('東亞')) {
      brand = 'Bank of East Asia';
    } else if (name.includes('建行') || desc.includes('建行')) {
      brand = '建行(亞洲)信用卡';
    } else {
      brand = 'Promotion';
    }
  }

  let unit = '$';
  let isFixed = false;
  let fixedAmt = 0;
  let mult = 0;
  let maxReward = 0;
  let minSpend = Number(promo.minimumSpending) || 0;
  let maxSpend = 999999;
  let remarks = '';
  const tiers = Array.isArray(promo.rewardTiers) ? promo.rewardTiers : [];
  const parsedTiers = tiers.map(parseRewardTier);
  parsedTiers.sort((a, b) => a.minSpending - b.minSpending);

  if (parsedTiers.length > 0) {
    for (const tier of parsedTiers) {
      const uom = String(tier.unitOfMeasure || '').toLowerCase();
      if (uom.includes('mile') || uom.includes('里')) {
        unit = 'miles';
      }
    }

    if (minSpend === 0) {
      minSpend = parsedTiers[0].minSpending;
    }
    if (parsedTiers.every(t => t.rewardType === 'Fixed')) {
      isFixed = true;
      fixedAmt = parsedTiers[0].fixedAmount;
    }
    if (!remarks) {
      remarks = `Tiered rebate: HK$${parsedTiers[0].fixedAmount} - HK$${parsedTiers[parsedTiers.length - 1].fixedAmount} (Min spend HK$${minSpend.toLocaleString()})`;
    }
  }

  if (name.includes('里') || name.toLowerCase().includes('miles')) {
    unit = 'miles';
  }

  // Direct MongoDB rewardScheme parsing
  if (Array.isArray(promo.rewardScheme) && promo.rewardScheme.length > 0) {
    const scheme = promo.rewardScheme[0];
    const rType = String(scheme.rewardType || '').toLowerCase();
    if (rType === 'fixed') {
      isFixed = true;
      fixedAmt = Number(scheme.fixedAmount) || 0;
      if (!minSpend && promo.minimumTargetAccumulatedSpending) {
        minSpend = Number(promo.minimumTargetAccumulatedSpending) || 0;
      }
      remarks = `Fixed rebate HK$${fixedAmt} (Min spend HK$${minSpend})`;
    } else if (rType === 'percentage') {
      isFixed = false;
      mult = Number(scheme.percentage) || 0;
      maxReward = Number(scheme.maxRewardAmount) || 0;
      remarks = `${mult}% rebate (Capped at HK$${maxReward})`;
    }
  }

  // Also check top-level promo maximumReward and maximumRewardPercentage
  if (promo.maximumReward && Number(promo.maximumReward) > 0) {
    maxReward = Number(promo.maximumReward);
  }
  if (promo.maximumRewardPercentage && Number(promo.maximumRewardPercentage) > 0 && mult === 0) {
    mult = Number(promo.maximumRewardPercentage);
  }

  if (formula) {
    if (formula.unit) {
      const fUnit = String(formula.unit).toLowerCase();
      unit = (fUnit.includes('mile') || fUnit.includes('里')) ? 'miles' : '$';
    }
    isFixed = Boolean(formula.isFixedReward);
    fixedAmt = Number(formula.fixedRewardAmount) || fixedAmt;
    mult = Number(formula.multiplier) || mult;
    maxReward = Number(formula.maxRewardAmount) || maxReward;
    minSpend = Number(formula.minSpending) || minSpend;
    maxSpend = Number(formula.maxSpending) || maxSpend;
    if (formula.remarks) remarks = formula.remarks;
  } else if (!remarks) {
    const lowerBrand = brand.toLowerCase();
    const lowerName = name.toLowerCase();

    if (lowerBrand.includes('alipay') || lowerName.includes('alipay')) {
      unit = '$';
      isFixed = true;
      fixedAmt = 100;
      minSpend = 5000;
      remarks = 'Spend HK$5,000 for HK$100 rebate';
    } else if (lowerBrand.includes('dbs') || lowerName.includes('dbs')) {
      unit = '$';
      isFixed = false;
      mult = 0.4;
      maxReward = 200;
      remarks = '0.4% cash rebate, capped at HK$200';
    } else if (lowerBrand.includes('east asia') || lowerBrand.includes('bea') || lowerName.includes('bea')) {
      unit = 'miles';
      remarks = '2,000 miles (retail spend HK$3k) or 4,000 miles (retail spend HK$20k)';
    }
  }

  return {
    id,
    brand,
    name,
    description: desc,
    unit,
    isFixedReward: isFixed,
    fixedRewardAmount: fixedAmt,
    multiplier: mult,
    maxRewardAmount: maxReward,
    minSpending: minSpend,
    maxSpending: maxSpend,
    startDate: promo.startDate,
    endDate: promo.endDate,
    banks,
    parentId,
    remarks,
    rewardTiers: tiers,
    parsedTiers
  };
}

/**
 * Main calculation function
 * @param {Object} params
 * @param {number} params.totalTax
 * @param {Array<{amount: number, dueDate?: string|Date}>} params.installments
 * @param {Array<TaxPromotion>} params.selectedPromotions
 * @param {boolean} params.crossPromotion
 * @param {'cash' | 'miles'} params.preference
 * @param {Date} [params.calculationDate]
 */
export function calculateTaxRebate({
  totalTax,
  installments = [],
  selectedPromotions = [],
  crossPromotion = true,
  preference = 'cash',
  calculationDate = null
}) {
  const preferredUnit = preference === 'miles' ? 'miles' : '$';
  const warnings = [];

  // Check for multiple selected promotions sharing the same bank or platform
  const bankPromoCount = new Map();
  for (const promo of selectedPromotions) {
    const grp = getBankOrPlatformGroup(promo);
    if (!bankPromoCount.has(grp)) bankPromoCount.set(grp, []);
    bankPromoCount.get(grp).push(promo);
  }
  for (const [grp, pList] of bankPromoCount.entries()) {
    if (pList.length > 1) {
      warnings.push(`Each customer might enjoy only 1 tax rebate promotion on the same bank/platform (${grp}).`);
    }
  }

  // Filter promotions by matching unit
  const eligiblePromos = selectedPromotions.filter(p => p.unit === preferredUnit);

  // Determine batches
  const batches = installments.length > 0
    ? installments.map((inst, idx) => ({
        index: idx + 1,
        amount: Number(inst.amount) || 0,
        dueDate: inst.dueDate ? new Date(inst.dueDate) : null
      }))
    : [{ index: 1, amount: Number(totalTax) || 0, dueDate: calculationDate ? new Date(calculationDate) : null }];

  // Single promotion evaluation for comparisons
  const singleResults = eligiblePromos.map(promo => {
    const res = evaluateSinglePromotion(promo, batches);
    if (res.hasExpiredInstallment) {
      warnings.push(`${promo.brand} expires before one of your scheduled installments.`);
    }
    return res;
  });

  singleResults.sort((a, b) => b.totalReward - a.totalReward);

  if (!crossPromotion) {
    const bestSingle = singleResults[0] || null;
    return {
      totalTax,
      preference,
      crossPromotion: false,
      grandTotalReward: bestSingle ? bestSingle.totalReward : 0,
      unit: preferredUnit,
      installmentResults: bestSingle ? bestSingle.installmentBreakdowns : [],
      singlePromotionResults: singleResults,
      calculationDate,
      warnings
    };
  }

  // Cross promotion for Air miles: takes best single miles promotion (miles promotions don't split)
  if (preferredUnit === 'miles') {
    const bestSingle = singleResults[0] || null;
    return {
      totalTax,
      preference,
      crossPromotion: true,
      grandTotalReward: bestSingle ? bestSingle.totalReward : 0,
      unit: 'miles',
      installmentResults: bestSingle ? bestSingle.installmentBreakdowns : [],
      singlePromotionResults: singleResults,
      calculationDate,
      warnings
    };
  }

  // Cross Promotion for Cash Rebate ($) using Combinatorial Threshold Solver
  const cumulativeSpent = new Map();
  const cumulativeReward = new Map();
  eligiblePromos.forEach(p => {
    cumulativeSpent.set(p.id, 0);
    cumulativeReward.set(p.id, 0);
  });

  const installmentResults = [];

  for (const batch of batches) {
    const activePromosOnDate = eligiblePromos.filter(p => {
      if (batch.dueDate && p.endDate) {
        return new Date(batch.dueDate) <= new Date(p.endDate);
      }
      return true;
    });

    const stepPromos = [];
    const percentPromos = [];

    for (const promo of activePromosOnDate) {
      const hasTiers = promo.parsedTiers && promo.parsedTiers.length > 0;
      if (hasTiers || promo.isFixedReward) {
        stepPromos.push(promo);
      } else if (promo.multiplier > 0) {
        percentPromos.push(promo);
      }
    }
    // Sort percentPromos descending by multiplier
    percentPromos.sort((a, b) => b.multiplier - a.multiplier);

    // Build candidate spends for each stepPromo
    const stepPromoCandidates = stepPromos.map(p => {
      const spentSoFar = cumulativeSpent.get(p.id) || 0;
      const candidates = [0];

      if (p.parsedTiers && p.parsedTiers.length > 0) {
        for (const tier of p.parsedTiers) {
          if (tier.minSpending > spentSoFar) {
            const needed = tier.minSpending - spentSoFar;
            if (needed <= batch.amount && !candidates.includes(needed)) {
              candidates.push(needed);
            }
          }
        }
      } else if (p.isFixedReward) {
        const rewardSoFar = cumulativeReward.get(p.id) || 0;
        if (rewardSoFar === 0 && p.minSpending > spentSoFar) {
          const needed = p.minSpending - spentSoFar;
          if (needed <= batch.amount && !candidates.includes(needed)) {
            candidates.push(needed);
          }
        }
      }

      candidates.sort((a, b) => a - b);
      return { promo: p, candidates };
    });

    let bestReward = -1;
    let bestTotalSpent = 0;
    let bestAllocations = [];

    function searchCombinations(promoIdx, currentSpendSum, currentSpends) {
      if (promoIdx === stepPromoCandidates.length) {
        let batchStepReward = 0;
        const currentAllocs = [];

        for (let i = 0; i < stepPromoCandidates.length; i++) {
          const promo = stepPromoCandidates[i].promo;
          const spend = currentSpends[i];
          if (spend > 0) {
            const spentSoFar = cumulativeSpent.get(promo.id) || 0;
            const rewardSoFar = cumulativeReward.get(promo.id) || 0;
            const evalRes = calculateRewardForSpend(promo, spentSoFar + spend);
            const incReward = Math.max(0, evalRes.reward - rewardSoFar);
            batchStepReward += incReward;
            currentAllocs.push({
              promotionId: promo.id,
              brand: promo.brand,
              promotionName: promo.name,
              amountSpent: spend,
              rewardEarned: Math.round(incReward * 100) / 100,
              unit: '$',
              remarks: evalRes.remarks || promo.remarks
            });
          }
        }

        let remainingForPercent = batch.amount - currentSpendSum;
        let batchPercentReward = 0;
        let percentSpentSum = 0;

        if (remainingForPercent > 0 && percentPromos.length > 0) {
          for (const p of percentPromos) {
            if (remainingForPercent <= 0) break;
            const spentSoFar = cumulativeSpent.get(p.id) || 0;
            const rewardSoFar = cumulativeReward.get(p.id) || 0;
            const maxCapSpend = p.maxRewardAmount > 0
              ? (p.maxRewardAmount / (p.multiplier / 100))
              : Infinity;
            const remainingCapSpend = Math.max(0, maxCapSpend - spentSoFar);
            if (remainingCapSpend > 0) {
              const spend = Math.min(remainingForPercent, remainingCapSpend);
              const rawReward = spend * (p.multiplier / 100);
              const rewardEarned = Math.round(rawReward * 100) / 100;

              remainingForPercent -= spend;
              percentSpentSum += spend;
              batchPercentReward += rewardEarned;

              const isCapped = (rewardSoFar + rewardEarned) >= p.maxRewardAmount && p.maxRewardAmount > 0;
              const remarks = isCapped
                ? `Hit max cap HK$${p.maxRewardAmount.toLocaleString()}`
                : `${p.multiplier}% rebate`;

              currentAllocs.push({
                promotionId: p.id,
                brand: p.brand,
                promotionName: p.name,
                amountSpent: spend,
                rewardEarned,
                unit: '$',
                remarks
              });
            }
          }
        }

        const totalBatchReward = Math.round((batchStepReward + batchPercentReward) * 100) / 100;
        const totalAllocated = currentSpendSum + percentSpentSum;

        if (totalBatchReward > bestReward || (totalBatchReward === bestReward && totalAllocated > bestTotalSpent)) {
          bestReward = totalBatchReward;
          bestTotalSpent = totalAllocated;
          bestAllocations = currentAllocs.map(a => ({ ...a }));
        }
        return;
      }

      const { candidates } = stepPromoCandidates[promoIdx];
      for (const spend of candidates) {
        if (currentSpendSum + spend > batch.amount) {
          break; // Candidates sorted ascending
        }
        currentSpends.push(spend);
        searchCombinations(promoIdx + 1, currentSpendSum + spend, currentSpends);
        currentSpends.pop();
      }
    }

    if (stepPromoCandidates.length > 0) {
      searchCombinations(0, 0, []);
    } else {
      // Only percent promos
      let remainingForPercent = batch.amount;
      let batchPercentReward = 0;
      let percentSpentSum = 0;
      const currentAllocs = [];

      for (const p of percentPromos) {
        if (remainingForPercent <= 0) break;
        const spentSoFar = cumulativeSpent.get(p.id) || 0;
        const rewardSoFar = cumulativeReward.get(p.id) || 0;
        const maxCapSpend = p.maxRewardAmount > 0
          ? (p.maxRewardAmount / (p.multiplier / 100))
          : Infinity;
        const remainingCapSpend = Math.max(0, maxCapSpend - spentSoFar);
        if (remainingCapSpend > 0) {
          const spend = Math.min(remainingForPercent, remainingCapSpend);
          const rawReward = spend * (p.multiplier / 100);
          const rewardEarned = Math.round(rawReward * 100) / 100;

          remainingForPercent -= spend;
          percentSpentSum += spend;
          batchPercentReward += rewardEarned;

          const isCapped = (rewardSoFar + rewardEarned) >= p.maxRewardAmount && p.maxRewardAmount > 0;
          const remarks = isCapped
            ? `Hit max cap HK$${p.maxRewardAmount.toLocaleString()}`
            : `${p.multiplier}% rebate`;

          currentAllocs.push({
            promotionId: p.id,
            brand: p.brand,
            promotionName: p.name,
            amountSpent: spend,
            rewardEarned,
            unit: '$',
            remarks
          });
        }
      }
      bestReward = Math.round(batchPercentReward * 100) / 100;
      bestTotalSpent = percentSpentSum;
      bestAllocations = currentAllocs;
    }

    // Allocate spillover if unallocated > 0
    let unallocated = batch.amount - bestTotalSpent;
    if (unallocated > 0) {
      if (bestAllocations.length > 0) {
        const lastAlloc = bestAllocations[bestAllocations.length - 1];
        lastAlloc.amountSpent += unallocated;
        unallocated = 0;
      } else if (activePromosOnDate.length > 0) {
        const firstPromo = activePromosOnDate[0];
        bestAllocations.push({
          promotionId: firstPromo.id,
          brand: firstPromo.brand,
          promotionName: firstPromo.name,
          amountSpent: unallocated,
          rewardEarned: 0,
          unit: '$',
          remarks: 'No additional rebate (below threshold)'
        });
        unallocated = 0;
      }
    }

    // Update cumulative spent and rewards
    for (const alloc of bestAllocations) {
      cumulativeSpent.set(alloc.promotionId, (cumulativeSpent.get(alloc.promotionId) || 0) + alloc.amountSpent);
      cumulativeReward.set(alloc.promotionId, (cumulativeReward.get(alloc.promotionId) || 0) + alloc.rewardEarned);
    }

    installmentResults.push({
      installmentIndex: batch.index,
      installmentAmount: batch.amount,
      dueDate: batch.dueDate,
      allocations: bestAllocations,
      subtotalReward: bestReward > 0 ? bestReward : 0,
      unit: '$'
    });
  }

  let grandTotalReward = Math.round(installmentResults.reduce((sum, r) => sum + r.subtotalReward, 0) * 100) / 100;

  // Safeguard: Cross promotion must never return less than the best single promotion!
  const bestSingle = singleResults[0] || null;
  if (bestSingle && bestSingle.totalReward > grandTotalReward) {
    grandTotalReward = bestSingle.totalReward;
    return {
      totalTax,
      preference,
      crossPromotion: true,
      grandTotalReward,
      unit: '$',
      installmentResults: bestSingle.installmentBreakdowns,
      singlePromotionResults: singleResults,
      calculationDate,
      warnings
    };
  }

  return {
    totalTax,
    preference,
    crossPromotion: true,
    grandTotalReward,
    unit: '$',
    installmentResults,
    singlePromotionResults: singleResults,
    calculationDate,
    warnings
  };
}

/**
 * Calculates reward and remarks for a single promotion and spent amount
 * @param {Object} promo
 * @param {number} cumulativeSpend
 * @returns {{reward: number, remarks: string}}
 */
export function calculateRewardForSpend(promo, cumulativeSpend) {
  if (cumulativeSpend <= 0) return { reward: 0, remarks: '' };

  // 1. Tiered promotion evaluation
  if (promo.parsedTiers && promo.parsedTiers.length > 0) {
    let matchedTier = null;
    for (const tier of promo.parsedTiers) {
      if (cumulativeSpend >= tier.minSpending) {
        matchedTier = tier;
      } else {
        break;
      }
    }

    if (!matchedTier) {
      return {
        reward: 0,
        remarks: `Below minimum spending (HK$${(promo.minSpending || 0).toLocaleString()})`
      };
    }

    if (matchedTier.rewardType === 'Fixed') {
      const label = matchedTier.conditionText || `HK$${matchedTier.minSpending.toLocaleString()}`;
      let finalReward = matchedTier.fixedAmount;
      if (promo.maxRewardAmount > 0 && finalReward > promo.maxRewardAmount) {
        finalReward = promo.maxRewardAmount;
      }
      return {
        reward: finalReward,
        remarks: `Tier reached: ${label} (HK$${finalReward.toLocaleString()})`
      };
    } else {
      const raw = cumulativeSpend * (matchedTier.percentage / 100);
      const cap = matchedTier.maxRewardAmount > 0 ? matchedTier.maxRewardAmount : promo.maxRewardAmount;
      const isCapped = cap > 0 && raw >= cap;
      const r = isCapped ? cap : raw;
      return {
        reward: Math.round(r * 100) / 100,
        remarks: `${matchedTier.percentage}% rebate${isCapped ? ` (Capped at HK$${cap.toLocaleString()})` : ''}`
      };
    }
  }

  // 2. Air miles (BEA benchmark or fixed threshold)
  if (promo.unit === 'miles') {
    if (cumulativeSpend >= 20000) {
      return { reward: 4000, remarks: '4,000 miles (requires HK$20,000 retail spend)' };
    } else if (cumulativeSpend >= 3000) {
      return { reward: 2000, remarks: '2,000 miles (requires HK$3,000 retail spend)' };
    }
    return { reward: 0, remarks: 'Below minimum miles spending (HK$3,000)' };
  }

  // 3. Fixed reward promotion (e.g. AlipayHK)
  if (promo.isFixedReward) {
    if (cumulativeSpend >= promo.minSpending) {
      return {
        reward: promo.fixedRewardAmount,
        remarks: `Fixed reward reached (HK$${promo.fixedRewardAmount.toLocaleString()})`
      };
    }
    return {
      reward: 0,
      remarks: `Below minimum spending (HK$${promo.minSpending.toLocaleString()})`
    };
  }

  // 4. Percentage promotion (e.g. DBS)
  if (promo.multiplier > 0) {
    const raw = cumulativeSpend * (promo.multiplier / 100);
    const isCapped = promo.maxRewardAmount > 0 && raw >= promo.maxRewardAmount;
    const r = isCapped ? promo.maxRewardAmount : raw;
    return {
      reward: Math.round(r * 100) / 100,
      remarks: isCapped
        ? `Hit max cap HK$${promo.maxRewardAmount.toLocaleString()}`
        : `${promo.multiplier}% rebate`
    };
  }

  return { reward: 0, remarks: '' };
}

/**
 * Evaluates an individual promotion across batches
 */
function evaluateSinglePromotion(promo, batches) {
  let cumulativeSpent = 0;
  let cumulativeReward = 0;
  let hasExpired = false;
  const breakdowns = [];

  for (const batch of batches) {
    if (batch.dueDate && promo.endDate && new Date(batch.dueDate) > new Date(promo.endDate)) {
      hasExpired = true;
      breakdowns.push({
        installmentIndex: batch.index,
        installmentAmount: batch.amount,
        dueDate: batch.dueDate,
        allocations: [{
          promotionId: promo.id,
          brand: promo.brand,
          promotionName: promo.name,
          amountSpent: batch.amount,
          rewardEarned: 0,
          unit: promo.unit,
          remarks: 'Promotion expired before due date'
        }],
        subtotalReward: 0,
        unit: promo.unit
      });
      continue;
    }

    cumulativeSpent += batch.amount;
    const evalResult = calculateRewardForSpend(promo, cumulativeSpent);
    const installmentReward = Math.max(0, evalResult.reward - cumulativeReward);
    cumulativeReward = evalResult.reward;

    const roundedInstReward = Math.round(installmentReward * 100) / 100;

    breakdowns.push({
      installmentIndex: batch.index,
      installmentAmount: batch.amount,
      dueDate: batch.dueDate,
      allocations: [{
        promotionId: promo.id,
        brand: promo.brand,
        promotionName: promo.name,
        amountSpent: batch.amount,
        rewardEarned: roundedInstReward,
        unit: promo.unit,
        remarks: evalResult.remarks || promo.remarks
      }],
      subtotalReward: roundedInstReward,
      unit: promo.unit
    });
  }

  return {
    promotion: promo,
    totalReward: Math.round(cumulativeReward * 100) / 100,
    unit: promo.unit,
    installmentBreakdowns: breakdowns,
    hasExpiredInstallment: hasExpired
  };
}

/**
 * Localizes remark text using translation function t
 * @param {string} remark
 * @param {Function} t
 * @returns {string}
 */
export function localizeTaxRemarks(remark, t) {
  if (!remark) return '';
  if (typeof t !== 'function') return remark;

  let text = String(remark).trim();

  // 1. "No additional rebate (below threshold)"
  if (text.includes('No additional rebate (below threshold)')) {
    return t('tax.no_additional_rebate');
  }

  // 2. "Tier reached: [label] (HK$[amount])"
  const tierMatch = text.match(/^Tier reached:\s*(.+?)\s*\(([^)]+)\)$/i);
  if (tierMatch) {
    const label = tierMatch[1].replace(/HK\$\s*/g, () => 'HK$\u00A0');
    const amount = tierMatch[2].replace(/HK\$\s*/g, () => 'HK$\u00A0');
    return `${t('tax.tier_reached')}: ${label} (${amount})`;
  }

  // 3. "Below minimum spending (HK$[amount])"
  const minMatch = text.match(/^Below minimum spending\s*\(([^)]+)\)$/i);
  if (minMatch) {
    const amount = minMatch[1].replace(/HK\$\s*/g, () => 'HK$\u00A0');
    return t('tax.below_min_spend', { amount });
  }

  // 4. "Hit max cap HK$[amount]"
  const capMatch = text.match(/^Hit max cap\s*(.+)$/i);
  if (capMatch) {
    const amount = capMatch[1].replace(/HK\$\s*/g, () => 'HK$\u00A0');
    return t('tax.hit_max_cap', { amount });
  }

  // 5. "Fixed reward reached (HK$[amount])"
  const fixMatch = text.match(/^Fixed reward reached\s*\(([^)]+)\)$/i);
  if (fixMatch) {
    const amount = fixMatch[1].replace(/HK\$\s*/g, () => 'HK$\u00A0');
    return t('tax.fixed_reward_reached', { amount });
  }

  // 6. "Promotion expired before due date"
  if (text.includes('Promotion expired before due date')) {
    return t('tax.promo_expired_due_date');
  }

  // 7. Miles benchmarks
  if (text.includes('4,000 miles (requires HK$20,000 retail spend)')) {
    return t('tax.miles_4k_reward');
  }
  if (text.includes('2,000 miles (requires HK$3,000 retail spend)')) {
    return t('tax.miles_2k_reward');
  }
  if (text.includes('Below minimum miles spending')) {
    return t('tax.below_min_miles_spend');
  }
  if (text.includes('2,000 miles (retail spend HK$3k) or 4,000 miles (retail spend HK$20k)')) {
    return t('tax.miles_bea_desc');
  }

  // 8. Percentage rebate with cap
  const pctCapMatch = text.match(/^(\d+(?:\.\d+)?%)\s*(?:cash\s*)?rebate(?:,\s*|\s*\(?)capped at\s*(.+?)\)?$/i);
  if (pctCapMatch) {
    const pct = pctCapMatch[1];
    const cap = pctCapMatch[2].replace(/HK\$\s*/g, () => 'HK$\u00A0');
    return `${pct} ${t('tax.rebate')} (${t('tax.capped_at')} ${cap})`;
  }

  // 9. Percentage rebate without cap
  const pctMatch = text.match(/^(\d+(?:\.\d+)?%)\s*(?:cash\s*)?rebate$/i);
  if (pctMatch) {
    return `${pctMatch[1]} ${t('tax.rebate')}`;
  }

  // 10. "Spend HK$5,000 for HK$100 rebate"
  const spendRebateMatch = text.match(/^Spend\s*(.+?)\s*for\s*(.+?)\s*rebate$/i);
  if (spendRebateMatch) {
    return t('tax.spend_x_for_y_rebate', {
      spend: spendRebateMatch[1].replace(/HK\$\s*/g, () => 'HK$\u00A0'),
      rebate: spendRebateMatch[2].replace(/HK\$\s*/g, () => 'HK$\u00A0')
    });
  }

  // 11. General replacements for phrases like "Tiered rebate", "Min spend", "Fixed rebate", "Capped at"
  text = text
    .replace(/Tiered rebate:/g, () => `${t('tax.tiered_rebate')}:`)
    .replace(/Tiered rebate/g, () => t('tax.tiered_rebate'))
    .replace(/Min spend/g, () => t('tax.min_spend'))
    .replace(/Fixed rebate/g, () => t('tax.fixed_rebate'))
    .replace(/Capped at/g, () => t('tax.capped_at'))
    .replace(/capped at/g, () => t('tax.capped_at'))
    .replace(/HK\$\s*/g, () => 'HK$\u00A0');

  return text;
}

/**
 * Format promotion subtitle description with localization
 * @param {Object} promo
 * @param {Function} t
 * @returns {string}
 */
export function formatPromoSubtitle(promo, t) {
  if (!promo) return '';
  if (typeof t !== 'function') return promo.remarks || '';

  if (promo.parsedTiers && promo.parsedTiers.length > 0) {
    const first = Number(promo.parsedTiers[0].fixedAmount || 0).toLocaleString();
    const last = Number(promo.parsedTiers[promo.parsedTiers.length - 1].fixedAmount || 0).toLocaleString();
    const minSp = promo.minSpending > 0 ? ` (${t('tax.min_spend')} HK\u00A0${Number(promo.minSpending).toLocaleString()})` : '';
    return `${t('tax.tiered_rebate')}: HK\u00A0${first} - HK\u00A0${last}${minSp}`;
  }

  if (promo.isFixedReward && promo.fixedRewardAmount > 0) {
    const minSp = promo.minSpending > 0 ? ` (${t('tax.min_spend')} HK\u00A0${Number(promo.minSpending).toLocaleString()})` : '';
    return `${t('tax.fixed_rebate')} HK\u00A0${Number(promo.fixedRewardAmount).toLocaleString()}${minSp}`;
  }

  if (promo.remarks) {
    return localizeTaxRemarks(promo.remarks, t);
  }

  return promo.unit === 'miles' ? t('tax.air_miles_promo') : t('tax.cash_rebate_promo');
}

